import { lazy, Suspense, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { Alert, Spin } from 'antd'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { translateEditor } from '../config/labels'

type Props = { content: string; onChange: (content: string) => void; disabled: boolean; preview: boolean }
type RichProps = Omit<Props, 'preview'>
const REMARK_PLUGINS = [remarkGfm]
const EMPHASIS: ('Bold' | 'Italic')[] = ['Bold', 'Italic']

/** Load the mature editing surface only when requested; preview never serializes Markdown. */
const RichText = lazy(async () => {
  const mdx = await import('@mdxeditor/editor')
  await import('@mdxeditor/editor/style.css')
  const toolbar = () => <><mdx.UndoRedo /><mdx.BlockTypeSelect /><mdx.BoldItalicUnderlineToggles options={EMPHASIS} />
    <mdx.ListsToggle /><mdx.CreateLink /><mdx.InsertTable /></>
  const plugins = [mdx.headingsPlugin(), mdx.listsPlugin(), mdx.quotePlugin(), mdx.linkPlugin(),
    mdx.linkDialogPlugin(), mdx.tablePlugin(), mdx.thematicBreakPlugin(), mdx.frontmatterPlugin(),
    mdx.codeBlockPlugin(), mdx.codeMirrorPlugin(), mdx.toolbarPlugin({ toolbarContents: toolbar })]
  const BoundEditor = ({ content, onChange, disabled }: RichProps) => {
    const [error, setError] = useState<string | null>(null)
    const [epoch, setEpoch] = useState(0)
    const emitted = useRef(content)
    const blocked = useRef(false)
    const live = useRef(true)
    const latest = useRef({ disabled, onChange })
    latest.current = { disabled, onChange }
    useLayoutEffect(() => { live.current = true; return () => { live.current = false } }, [])
    // Remount only for an external replacement. Echoes of our own edits retain cursor/history.
    useLayoutEffect(() => {
      if (content === emitted.current) return
      emitted.current = content; blocked.current = false; setError(null); setEpoch((value) => value + 1)
    }, [content])
    const currentEpoch = useRef(epoch)
    currentEpoch.current = epoch
    const changed = useCallback((markdown: string, initialNormalize: boolean) => {
      if (!live.current || currentEpoch.current !== epoch || latest.current.disabled || initialNormalize || blocked.current) return
      if (markdown === emitted.current) return
      emitted.current = markdown; latest.current.onChange(markdown)
    }, [epoch])
    const failed = useCallback(({ error: message }: { error: string }) => {
      if (live.current && currentEpoch.current === epoch) { blocked.current = true; setError(message) }
    }, [epoch])
    return <div className="instance-administration__markdown-rich">
      {error ? <Alert type="warning" showIcon title="此内容暂不能安全可视编辑，请切换源码" description={error} /> : null}
      <mdx.MDXEditor translation={translateEditor} key={epoch} markdown={content} onChange={changed} onError={failed} plugins={plugins}
        readOnly={disabled || !!error} suppressHtmlProcessing trim={false}
        contentEditableClassName="instance-administration__markdown-content" />
    </div>
  }
  return { default: BoundEditor }
})

export const MarkdownEditor = ({ content, onChange, disabled, preview }: Props) => preview
  ? <article className="instance-administration__markdown-preview" aria-label="文档预览">
    <ReactMarkdown remarkPlugins={REMARK_PLUGINS} skipHtml>{content}</ReactMarkdown>
  </article>
  : <Suspense fallback={<Spin />}><RichText content={content} onChange={onChange} disabled={disabled} /></Suspense>
